import './style.scss';
import '../style.scss';
import { Button, ButtonFill, ButtonSize, ButtonType } from 'views/mutual/button';
import { Caption } from 'views/mutual/caption';
import { CorporateAccountOpenProfileDetailsPages, useCorporateAccountOpenProfileDetailsPageTypeStore } from 'views/pages/accountOpenProfileDetails/corporateProfileDetails/hooks';
import { InputTemplate, InputTemplateDataProps, MaskType } from 'views/templates/module/inputTemplate';
import { AddressInput, AddressInputType } from 'views/templates/module/addressInput';
import { AboutRulerType, RulerImportantPublicWorkerType, useRulerInfo } from './hooks';
import { FormContentHeader, FormType } from 'views/templates/module/contentHeader';
import { MultiSelections, MultiSelectionsDataProps } from 'views/mutual/inputRadio';
import { TemplateProps } from '../common';
import { RelastionShip, RelastionShipList, findRelastionShipName } from 'views/utils/enums';
import { DateInput, DateInputProps } from 'views/mutual/dateInput';
import { useCustomLocationName } from 'views/mutual/location/hooks';
import { FormErrorScrollTarget } from 'views/mutual/formErrorScroll';

type RulerDetailInfoProps = {
  index: number;
  aboutRulerProps: MultiSelectionsDataProps;
  relationshipProps: MultiSelectionsDataProps;
  addressProps: AddressInputType;
  nameProps: InputTemplateDataProps;
  birthDayProps: DateInputProps;
  phoneNumberProps: InputTemplateDataProps;
  mailAddressProps: InputTemplateDataProps;
  confirmMailAddressProps: InputTemplateDataProps;
  importantPublicWorker: any;
};
export const RulerDetailInfo = ({
  index,
  aboutRulerProps,
  relationshipProps,
  addressProps,
  nameProps,
  birthDayProps,
  phoneNumberProps,
  mailAddressProps,
  confirmMailAddressProps,
  importantPublicWorker,
  isUpdate,
}: RulerDetailInfoProps & TemplateProps) => {
  return (
    <>
      <FormContentHeader label="実質的支配者について" formType={FormType.Required} />
      <div className="accountOpenTemplate_explanation">
        お客様の実質的支配者について、下記のリンク先をご確認のうえご申告ください
        <br />
        <a
          className="accountOpenTemplate_input_wrapper_link"
          href={process.env.REACT_APP_ABOUT_RULER_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          実質的支配者該当性をご判断いただくにあたって（法人のお客様）
        </a>
      </div>
      <div className="accountOpenTemplate_input_check">
        <MultiSelections
          items={[
            {
              label: '法人を代表し、その業務を執行する個人',
              value: AboutRulerType.Same,
            },
            {
              label: '代表者とは異なる個人、または上場法人等に該当',
              value: AboutRulerType.Different,
            },
          ]}
          name={`AboutRuler${index}`}
          {...aboutRulerProps}
        />
      </div>

      {/* エラー時はHeaderに合わせるためにFormErrorScrollTargetを配置 */}
      <FormErrorScrollTarget error={relationshipProps.error} position="start" />
      <FormContentHeader label="実質的支配者と貴社とのご関係" formType={FormType.Required} />
      <div className="accountOpenTemplate_input_wrapper">
        <MultiSelections
          items={RelastionShipList.filter(
            (x) =>
              findRelastionShipName(x.id) !== RelastionShip.Daihyosya ||
              aboutRulerProps.selected === AboutRulerType.Same,
          ).map((x) => {
            return {
              label: x.value,
              value: x.id,
            };
          })}
          name={`RulerRelationShip${index}`}
          {...relationshipProps}
        />
      </div>

      {aboutRulerProps.selected === AboutRulerType.Different && (
        <>
          <FormContentHeader label="氏名（上場法人等の場合は名称）" formType={FormType.Required} conditional={true} />
          <div className="accountOpenTemplate_input_wrapper">
            <InputTemplate {...nameProps} placeholder="入力してください" />
          </div>

          <FormContentHeader
            label="生年月日　※上場法人の場合は1900/01/01とご記入ください"
            formType={FormType.Required}
            conditional={true}
          />
          <div className="accountOpenTemplate_input_wrapper">
            <DateInput placeholder={'年/月/日'} showYearMonthDropdown={true} {...birthDayProps} />
          </div>          

          <FormContentHeader label="メールアドレス" formType={FormType.Optional} conditional={true} />
          <div className="accountOpenTemplate_input_wrapper">
            <InputTemplate
              type="text"
              placeholder="例：example@example.com"
              masked={isUpdate ? { type: MaskType.End, displayedCount: 4 } : undefined}
              {...mailAddressProps}
            />
          </div>
          <FormContentHeader label="メールアドレス（確認用）" formType={FormType.Optional} conditional={true} />
          <div className="accountOpenTemplate_input_wrapper">
            <InputTemplate
              type="text"
              placeholder="例：example@example.com"
              masked={isUpdate ? { type: MaskType.End, displayedCount: 4 } : undefined}
              {...confirmMailAddressProps}
            />
          </div>

          <FormContentHeader
            label="住所（上場法人の場合は本店所在地）"
            formType={FormType.Required}
            conditional={true}
          />
          <div className="accountOpenTemplate_input_wrapper">
            <AddressInput {...addressProps} />
          </div>
        </>
      )}

      <FormContentHeader label="外国の重要な公人への該当性" formType={FormType.Required} />
      <div className="accountOpenTemplate_explanation">
        外国の重要な公人への該当性は、下記リンク先をご確認のうえご申告ください
        <br />
        <a
          className="accountOpenTemplate_input_wrapper_link"
          href={process.env.REACT_APP_POLITICALLY_EXPOSED_PERSONS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          「外国の重要な公人該当性」欄のご記入にあたって（法人・個人のお客様共通）
        </a>
      </div>
      <div className="accountOpenTemplate_input_check">
        <MultiSelections
          items={[
            {
              label: '上記の実質的支配者は、外国の重要な公人もしくはその親族には該当しません',
              value: RulerImportantPublicWorkerType.No,
            },
            {
              label: '上記の実質的支配者は、外国の重要な公人もしくはその親族に該当します',
              value: RulerImportantPublicWorkerType.Yes,
            },
          ]}
          name={`ImportantPublicWorker${index}`}
          {...importantPublicWorker}
        />
      </div>
    </>
  );
};

export type RulerInfoProps = {
  formik: any;
  ruler1Props: any;
  addRuler2: any;
  ruler2Props: any;
  addRuler3: any;
  ruler3Props: any;
};

export const RulerInfoWrapper = () => {
  return <RulerInfo {...useRulerInfo({ isUpdate: false })} />;
};

export const RulerInfo = ({
  formik,
  ruler1Props,
  addRuler2,
  ruler2Props,
  addRuler3,
  ruler3Props,
  isUpdate,
}: RulerInfoProps & TemplateProps) => {
  const { setAccountOpenProfileDetailsPageType } = useCorporateAccountOpenProfileDetailsPageTypeStore();

  useCustomLocationName('口座開設申込みフォーム');

  return (
    <form onSubmit={formik.handleSubmit} className="accountOpenTemplate">
      {!isUpdate && <Caption label="実質的支配者のご申告" />}
      <div className="accountOpenTemplate_input">
        <RulerDetailInfo index={1} {...ruler1Props} isUpdate={isUpdate} />

        <FormContentHeader label="実質的支配者②" formType={FormType.Optional} />
        <div className="accountOpenTemplate_input_wrapper">
          <button type="button" className={formik.values.addtionalRuler2 ? 'deleteButton' : 'addButton'} {...addRuler2}>
            {formik.values.addtionalRuler2 ? '削除' : '追加'}
          </button>
        </div>
        {formik.values.addtionalRuler2 && <RulerDetailInfo index={2} {...ruler2Props} isUpdate={isUpdate} />}

        {formik.values.addtionalRuler2 && 
        <>
        <FormContentHeader label="実質的支配者③" formType={FormType.Optional} />
        <div className="accountOpenTemplate_input_wrapper">
          <button type="button" className={formik.values.addtionalRuler3 ? 'deleteButton' : 'addButton'} {...addRuler3}>
            {formik.values.addtionalRuler3 ? '削除' : '追加'}
          </button>
        </div>
        {formik.values.addtionalRuler3 && <RulerDetailInfo index={3} {...ruler3Props} isUpdate={isUpdate} />}
        </>
        }
      </div>
      {!isUpdate && (
        <div className="accountOpenTemplate_button">
          <Button
            type={ButtonType.Submit}
            label="投資家区分の確認へ進む"
            buttonFill={ButtonFill.black}
            buttonSize={ButtonSize.medium}
          />
          <Button
            label="前の画面に戻る"
            buttonFill={ButtonFill.link}
            buttonSize={ButtonSize.small}
            onClick={() => setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.dealerInfo)}
          />
        </div>
      )}
    </form>
  );
};
